import { useEffect, useMemo, useState } from "react";

function splitCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    const next = line[i + 1];

    if (ch === '"' && inQuotes && next === '"') {
      cur += '"';
      i++;
      continue;
    }
    if (ch === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
      continue;
    }
    cur += ch;
  }
  out.push(cur);
  return out;
}

function parseCsv(csv) {
  const lines = csv.split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return [];

  const headers = splitCsvLine(lines[0]).map((h) => h.trim());

  return lines.slice(1).map((line) => {
    const cols = splitCsvLine(line);
    const row = {};
    headers.forEach((h, i) => {
      row[h] = (cols[i] ?? "").trim();
    });
    return row;
  });
}

function formatDate(iso) {
  // iso like 2026-01-05
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function platformString(r) {
  const items = [
    r.Facebook === "TRUE" ? "FB" : null,
    r["Twitter/X"] === "TRUE" ? "X" : null,
    r.LinkedIn === "TRUE" ? "LI" : null,
    r.Instagram === "TRUE" ? "IG" : null,
  ].filter(Boolean);

  return items.join(" • ");
}

function normalizeType(t) {
  const s = (t || "").toLowerCase();
  if (s.includes("cred")) return { label: "Credibility", className: "bg-emerald-50 text-emerald-700" };
  if (s.includes("educ")) return { label: "Educational", className: "bg-blue-50 text-blue-700" };
  if (s.includes("cta")) return { label: "CTA", className: "bg-amber-50 text-amber-700" };
  if (s.includes("person")) return { label: "Personality", className: "bg-purple-50 text-purple-700" };
  return { label: t || "Post", className: "bg-gray-50 text-gray-700" };
}

export default function StarterCalendarPreview() {
  const [rows, setRows] = useState([]);
  const [week, setWeek] = useState("1");

  const csvPath = "/DobieCore_4-Week_Starter_Content_Calendar.csv";

  useEffect(() => {
  fetch(csvPath, { cache: "no-store" })
    .then((r) => r.text())
    .then((t) => setRows(parseCsv(t)))
    .catch(() => setRows([]));
}, []);



  const prettyRows = useMemo(() => {
  return (rows || []).map((r) => {
    const type = normalizeType(r.post_type);
    return {
      week: r.week,
      day: r.day,
      dateIso: r.date,
      datePretty: formatDate(r.date),
      time: r.time,
      typeLabel: type.label,
      typeClass: type.className,
      topic: (r.text || "").replace(/\s+/g, " ").trim(),
      platforms: platformString(r),
    };
  });
}, [rows]);



  const validRows = useMemo(() => {
  return (prettyRows || []).filter((r) => {
    const weekOk = /^\d+$/.test(String(r.week || "").trim());
    const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(String(r.dateIso || "").trim());
    const topicOk = (r.topic || "").length > 0;
    return weekOk && dateOk && topicOk;
  });
}, [prettyRows]);




  const weeksAvailable = useMemo(() => {
  const set = new Set(
    (validRows || [])
      .map((r) => String(r.week || "").trim())
      .filter((w) => /^\d+$/.test(w))
  );
  return Array.from(set).sort((a, b) => Number(a) - Number(b));
}, [validRows]);


useEffect(() => {
  if (weeksAvailable.length && !weeksAvailable.includes(week)) {
    setWeek(weeksAvailable[0]);
  }
}, [weeksAvailable, week]);



const filtered = useMemo(() => {
  return (validRows || [])
    .filter((r) => r.week === week)
    .sort((a, b) => (a.dateIso || "").localeCompare(b.dateIso || ""));
}, [validRows, week]);


    if (!validRows.length) {
    return (
      <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50 p-4 text-sm text-gray-600">
        Preview unavailable (couldn’t load the CSV). You can still download it below.
      </div>
    );
  }

  return (
    <div className="mt-4">
      {/* Week selector */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Preview
        </span>
        <div className="flex gap-2">
          {(weeksAvailable || []).map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setWeek(w)}
              className={[
                "text-xs font-semibold px-3 py-1 rounded-full border transition-colors",
                week === w
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-700 border-gray-200 hover:border-gray-300",
              ].join(" ")}
            >
              Week {w}
            </button>
          ))}
        </div>
      </div>

      {/* Helpful mini table */}
      <div className="overflow-x-auto rounded-lg border border-gray-100">
        <table className="min-w-full text-sm">
          <thead className="text-left text-gray-500 bg-gray-50">
            <tr>
              <th className="py-2 px-3">Day</th>
              <th className="py-2 px-3">Date</th>
              <th className="py-2 px-3">Type</th>
              <th className="py-2 px-3">Topic</th>
              <th className="py-2 px-3">Platforms</th>
            </tr>
          </thead>
          <tbody className="text-gray-800">
            {(filtered || []).map((r, idx) => (
              <tr key={idx} className="border-t border-gray-100 align-top">
                <td className="py-2 px-3 whitespace-nowrap">{r.day}</td>
                <td className="py-2 px-3 whitespace-nowrap">{r.datePretty}</td>
                <td className="py-2 px-3">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${r.typeClass}`}>
                    {r.typeLabel}
                  </span>
                </td>
                <td className="py-2 px-3 min-w-[260px]">
                  <div className="font-medium text-gray-900">{r.topic}</div>
                  <div className="text-xs text-gray-500 mt-1">{r.time}</div>
                </td>
                <td className="py-2 px-3 whitespace-nowrap text-gray-600">{r.platforms}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Tiny helper line */}
      <p className="text-xs text-gray-500 mt-2">
        Download the CSV to import into your scheduler. Captions and images are generated separately.
      </p>
    </div>
  );
}
